import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {forkJoin, Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {GetCountriesGQL, GetUnitiesByCountryGQL} from '../../generated/graphql';

/** Mesmo id em homologação e produção (ver migration CountryClusterActive da API). */
export const BRASIL_ID = '7b72084f-5443-49b3-9fc2-41278f290aa9';

/** Usado quando o Brasil não vem em getCountries (país inativo); mesma bandeira do cadastro do país. */
const BRASIL_FALLBACK = {name: 'Brasil', flag: 'https://euro-clic.s3.sa-east-1.amazonaws.com/country/brazil.png'};

export interface CountryCard {
  id: string;
  name?: string;
  /** Chave de tradução do nome; usada pelo card sintético de unidades internacionais. */
  nameKey?: string;
  flag: string;
  /** Ids dos países cujas unidades o card lista. */
  countryIds: string[];
}

export interface CountryUnity {
  id: string;
  name: string;
  /** País real da unidade, gravado na ideia. */
  countryId: string;
}

/**
 * Passo "país e unidade" do cadastro de ideia: mostra só Brasil e
 * "Unidades Internacionais", que agrupa as unidades dos demais países ativos.
 */
@Injectable({
  providedIn: 'root'
})
export class CountryUnityService {

  constructor(private getCountriesGQL: GetCountriesGQL,
              private getUnitiesByCountryGQL: GetUnitiesByCountryGQL,
              private translate: TranslateService) {
  }

  getCountryCards(): Observable<CountryCard[]> {
    return this.getCountriesGQL.fetch().pipe(map(({data}) => {
      const countries = data.getCountries;
      const cards: CountryCard[] = [];

      // O card do Brasil aparece sempre, mesmo com o país inativo no banco (caso de homologação).
      const brasil = countries.find(c => c.id === BRASIL_ID) || BRASIL_FALLBACK;
      cards.push({id: BRASIL_ID, name: brasil.name, flag: brasil.flag, countryIds: [BRASIL_ID]});

      const internationalIds = countries.filter(c => c.id !== BRASIL_ID).map(c => c.id);
      if (internationalIds.length > 0) {
        cards.push({
          id: 'international',
          nameKey: 'unity.international',
          flag: 'assets/images/unidades-internacionais.png',
          countryIds: internationalIds
        });
      }

      return cards.sort((a, b) => this.compareNames(this.cardName(a), this.cardName(b)));
    }));
  }

  getUnities(card: CountryCard): Observable<CountryUnity[]> {
    if (!card || card.countryIds.length === 0) {
      return of([]);
    }

    const requests = card.countryIds.map(countryId =>
      this.getUnitiesByCountryGQL.fetch({id: countryId}).pipe(
        map(({data}) => data.getUnitiesByCountry.map(u => ({id: u.id, name: u.name, countryId})))
      )
    );

    return forkJoin(requests).pipe(
      map(lists => ([] as CountryUnity[]).concat(...lists).sort((a, b) => this.compareNames(a.name, b.name)))
    );
  }

  private cardName(card: CountryCard): string {
    return card.nameKey ? this.translate.instant(card.nameKey) : card.name;
  }

  private compareNames(a: string, b: string): number {
    return (a || '').localeCompare(b || '', 'pt', {sensitivity: 'base'});
  }
}
